import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrivacyNotice = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const privacyPoints = [
    {
      icon: 'Shield',
      title: 'Secure Authentication',
      description: 'We use OAuth 2.0 for secure account connections. Your passwords are never stored or accessed.'
    },
    {
      icon: 'Lock',
      title: 'Data Protection',
      description: 'Your playlist data is encrypted in transit and only used for synchronization purposes.'
    },
    {
      icon: 'Eye',
      title: 'Limited Access',
      description: 'We only request permissions needed for playlist management. No access to personal messages or private data.'
    },
    {
      icon: 'Trash2',
      title: 'Data Control',
      description: 'You can disconnect accounts and delete your data at any time from your account settings.'
    }
  ];

  const permissions = {
    spotify: [
      'Read your playlists and saved music',
      'Create and modify playlists',
      'Access your profile information',
      'View your listening history'
    ],
    youtube: [
      'Read your YouTube Music playlists',
      'Create and manage playlists',
      'Access your channel information',
      'View your music library'
    ]
  };

  return (
    <div className="bg-surface border border-border rounded-xl shadow-elevation-1">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="Shield" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Privacy & Security</h3>
              <p className="text-sm text-text-secondary">How we protect your data</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconSize={16}
          >
            {isExpanded ? 'Less Info' : 'More Info'}
          </Button>
        </div>
      </div>

      {/* Privacy Points */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {privacyPoints.map((point, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-background rounded-lg border border-border">
              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg flex-shrink-0 mt-0.5">
                <Icon name={point.icon} size={16} className="text-primary" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-text-primary mb-1">{point.title}</h4>
                <p className="text-xs text-text-secondary leading-relaxed">{point.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="space-y-6">
            {/* Permissions Breakdown */}
            <div>
              <h4 className="text-sm font-medium text-text-primary mb-4 flex items-center space-x-2">
                <Icon name="Key" size={16} className="text-primary" />
                <span>Required Permissions</span>
              </h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {Object.entries(permissions).map(([platform, perms]) => (
                  <div key={platform} className="p-4 bg-background rounded-lg border border-border">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon 
                        name={platform === 'spotify' ? 'Music' : 'Play'} 
                        size={16} 
                        className={platform === 'spotify' ? 'text-success' : 'text-error'}
                      />
                      <span className="text-sm font-medium text-text-primary capitalize">{platform}</span>
                    </div>
                    <ul className="space-y-2">
                      {perms.map((permission, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <Icon name="Check" size={12} className="text-success mt-1 flex-shrink-0" />
                          <span className="text-xs text-text-secondary">{permission}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Usage */}
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <h4 className="text-sm font-medium text-primary mb-2 flex items-center space-x-2">
                <Icon name="Info" size={16} />
                <span>How We Use Your Data</span>
              </h4>
              <ul className="space-y-1 text-xs text-text-secondary">
                <li>• Playlist synchronization between connected platforms</li>
                <li>• Song matching and duplicate detection</li>
                <li>• Transfer history and progress tracking</li>
                <li>• Account connection status monitoring</li>
                <li>• Error reporting and sync optimization</li>
              </ul>
            </div>

            {/* Contact & Support */}
            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div>
                <p className="text-sm font-medium text-text-primary">Questions about privacy?</p>
                <p className="text-xs text-text-secondary">Contact our support team for more information</p>
              </div>
              <Button variant="outline" size="sm" iconName="Mail" iconSize={14}>
                Contact Support
              </Button>
            </div>
          </div>
        )}

        {/* Quick Summary */}
        {!isExpanded && (
          <div className="text-center">
            <p className="text-xs text-text-muted">
              We prioritize your privacy and use industry-standard security measures to protect your data.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyNotice;